import React, { useState } from "react";
import { BellIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { Line } from "react-chartjs-2";
import { Tooltip } from "react-tooltip";
import Modal from "../../../components/shared/Modal";
import SubPageLayout from "../../../components/shared/SubPageLayout";

// Notification Component
const NotificationBell = ({ count }) => (
  <div className="relative">
    <BellIcon className="h-6 w-6 text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300 cursor-pointer" />
    {count > 0 && (
      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
        {count}
      </span>
    )}
  </div>
);

// Global Search Component
const GlobalSearch = () => (
  <div className="relative flex-1 max-w-lg">
    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
    </div>
    <input
      type="text"
      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-surface-default dark:bg-surface-default placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
      placeholder="Search clients, tasks, or documents..."
    />
  </div>
);

// Enhanced Dashboard Tab with Charts
const DashboardTab = () => {
  const [timeRange, setTimeRange] = useState("6mo");
  const [isLoading, setIsLoading] = useState(false);

  // Sample data for the chart
  const chartData = {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    datasets: [
      {
        label: "Tax Savings",
        data: [12000, 19000, 15000, 25000, 22000, 30000],
        fill: false,
        borderColor: "rgb(59, 130, 246)",
        tension: 0.1,
      },
      {
        label: "RMD Completion Rate",
        data: [30, 45, 50, 60, 62, 65],
        fill: false,
        borderColor: "rgb(34, 197, 94)",
        tension: 0.1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Tax Planning Metrics",
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            if (context.dataset.label === "Tax Savings") {
              label += new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(context.parsed.y);
            } else {
              label += context.parsed.y + "%";
            }
            return label;
          },
        },
      },
    },
    scales: {
      y: {
        type: "linear",
        display: true,
        position: "left",
        ticks: {
          callback: function (value) {
            return "$" + value.toLocaleString();
          },
        },
      },
      y1: {
        type: "linear",
        display: true,
        position: "right",
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          callback: function (value) {
            return value + "%";
          },
        },
      },
    },
  };

  // KPI data
  const kpis = [
    {
      label: "Total Clients",
      value: "127",
      change: "+3 this month",
      trend: "up",
    },
    {
      label: "Open Tasks",
      value: "23",
      change: "5 high priority",
      trend: "neutral",
    },
    {
      label: "Total Tax Savings",
      value: "$1.2M",
      change: "+15% YoY",
      trend: "up",
    },
    {
      label: "Revenue from Referrals",
      value: "$45K",
      change: "+5K this quarter",
      trend: "up",
    },
  ];

  return (
    <div className="space-y-6">
      {/* KPI Bar */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi, index) => (
          <div key={index} className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-4">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{kpi.label}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{kpi.value}</p>
              </div>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  kpi.trend === "up"
                    ? "bg-green-100 text-green-800"
                    : kpi.trend === "down"
                      ? "bg-red-100 text-red-800"
                      : "bg-gray-100 text-gray-800 dark:text-gray-200"
                }`}
              >
                {kpi.change}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Analytics Overview */}
      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Analytics Overview
          </h3>
          <div className="flex items-center space-x-2">
            {["3mo", "6mo", "1yr", "YTD"].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1 text-sm rounded-md ${
                  timeRange === range
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 dark:text-gray-300 hover:bg-gray-200"
                }`}
              >
                {range}
              </button>
            ))}
          </div>
        </div>
        <div className="h-64">
          {isLoading ? (
            <div className="h-full flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <Line data={chartData} options={chartOptions} />
          )}
        </div>
        <div className="mt-4 flex justify-end">
          <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
            View Detailed Analytics →
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Tasks & Notifications */}
        <div className="col-span-2 lg:col-span-1 bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Tasks & Notifications
            </h3>
            <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              View All Tasks →
            </button>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
              <div className="flex items-start space-x-3">
                <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-yellow-200">
                  <span className="text-yellow-800 font-medium text-xs">!</span>
                </span>
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    Client X RMD Incomplete
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Due by December 31, 2024
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  className="text-gray-400 hover:text-gray-500 dark:text-gray-500"
                  data-tooltip-id="task-tooltip"
                  data-tooltip-content="Snooze for 1 week"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6l4 2"
                    />
                  </svg>
                </button>
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  data-tooltip-id="task-tooltip"
                  data-tooltip-content="Complete Required Minimum Distribution"
                >
                  Complete RMD
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
              <div className="flex items-start space-x-3">
                <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-red-200">
                  <span className="text-red-800 font-medium text-xs">H</span>
                </span>
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    Missing Form 7216 - Johnson Family
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Required for tax document sharing
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  className="text-gray-400 hover:text-gray-500 dark:text-gray-500"
                  data-tooltip-id="task-tooltip"
                  data-tooltip-content="Mark as low priority"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  data-tooltip-id="task-tooltip"
                  data-tooltip-content="Send Form 7216 Request"
                >
                  Send Request
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Deadlines */}
        <div className="col-span-2 lg:col-span-1 bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Upcoming Deadlines
            </h3>
            <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              View Calendar →
            </button>
          </div>
          <div className="space-y-4">
            <div className="border-l-4 border-red-500 pl-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    Q1 Estimated Tax Payments
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Due April 15, 2024</p>
                </div>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  15 days left
                </span>
              </div>
            </div>
            <div className="border-l-4 border-yellow-500 pl-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    2023 Tax Return Filing
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Due April 15, 2024</p>
                </div>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  15 days left
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick RMD Overview */}
        <div className="col-span-2 lg:col-span-1 bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              RMD Overview
            </h3>
            <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              Process RMDs →
            </button>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">Total RMDs Due</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">$245,000</p>
              </div>
              <div className="text-right">
                <p className="font-medium text-gray-900 dark:text-gray-100">Completed</p>
                <div className="flex items-center space-x-2">
                  <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-600 rounded-full"
                      style={{ width: "65%" }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-green-600">
                    65%
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                Top Outstanding RMDs
              </p>
              <div className="space-y-2">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Smith Family</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">$45,000</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Johnson Family</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">$32,000</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Williams Family</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">$28,000</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Revenue Opportunities */}
        <div className="col-span-2 lg:col-span-1 bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Revenue Opportunities
            </h3>
            <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              View All →
            </button>
          </div>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    Roth Conversion Opportunities
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">15 clients eligible</p>
                  <p className="text-sm font-medium text-green-600 mt-1">
                    Potential Revenue: $25,000
                  </p>
                </div>
                <div className="flex flex-col items-end">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mb-2">
                    High ROI
                  </span>
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                    Create Recommendations
                  </button>
                </div>
              </div>
              <div className="mt-2">
                <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-600 rounded-full"
                    style={{ width: "40%" }}
                  ></div>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  6 of 15 clients implemented
                </p>
              </div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    Tax Loss Harvesting
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">8 accounts eligible</p>
                  <p className="text-sm font-medium text-green-600 mt-1">
                    Potential Savings: $15,000
                  </p>
                </div>
                <div className="flex flex-col items-end">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mb-2">
                    Quick Win
                  </span>
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                    View Opportunities
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Tax Professional Engagement Modal
const TaxProEngagementModal = ({ professional, onClose }) => {
  const handleEngagement = () => {
    // Handle engagement logic
    onClose();
  };

  return (
    <Modal 
      isOpen={!!professional} 
      onClose={onClose} 
      width="large"   // Use standardized large size for professional details
    >
      <div className="p-6">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Engage with {professional?.name}
        </h3>
        <div className="space-y-4">
          <div className="flex items-start space-x-4">
            <img
              src={professional?.avatar}
              alt={professional?.name}
              className="h-12 w-12 rounded-full"
            />
            <div>
              <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {professional?.name}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {professional?.specialization}
              </p>
            </div>
          </div>
          <div>
            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Engagement Details
            </h5>
            <textarea
              className="w-full h-32 p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none bg-white dark:bg-neutral-800 text-gray-900 dark:text-gray-100"
              placeholder="Describe your tax planning needs and requirements..."
            />
          </div>
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-neutral-700 hover:bg-gray-200 dark:hover:bg-neutral-600 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleEngagement}
              className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
            >
              Send Engagement Request
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const TaxProNetworkTab = () => {
  const [showEngagementModal, setShowEngagementModal] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState(null);

  // Sample professional data
  const professionals = [
    {
      id: 1,
      name: "John Smith, CPA",
      location: "New York, NY",
      specialization: "High-net-worth tax planning",
      experience: "15 years",
      hourlyRate: 250,
      status: "available",
      is1099: true,
      riaSplitPercent: 10,
      advisorSplitPercent: 10,
      capacity: "accepting",
      expertise: ["Individual", "Estate", "Trust"],
    },
    {
      id: 2,
      name: "Sarah Johnson, EA",
      location: "California",
      specialization: "Business tax strategy",
      experience: "12 years",
      hourlyRate: 200,
      status: "limited",
      is1099: true,
      riaSplitPercent: 10,
      advisorSplitPercent: 10,
      capacity: "limited",
      expertise: ["Business", "International", "Startup"],
    },
    {
      id: 3,
      name: "Michael Chen, CPA",
      location: "Texas",
      specialization: "International tax",
      experience: "8 years",
      hourlyRate: 225,
      status: "available",
      is1099: true,
      riaSplitPercent: 10,
      advisorSplitPercent: 10,
      capacity: "accepting",
      expertise: ["International", "Individual", "Compliance"],
    },
  ];

  const handleEngageClick = (professional) => {
    setSelectedProfessional(professional);
    setShowEngagementModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search tax professionals..."
              className="w-full p-2 border rounded-md text-gray-900 dark:text-gray-100"
            />
          </div>
          <select className="p-2 border rounded-md text-gray-900 dark:text-gray-100 min-w-[150px]">
            <option>All Locations</option>
            <option>New York</option>
            <option>California</option>
            <option>Texas</option>
          </select>
          <select className="p-2 border rounded-md text-gray-900 dark:text-gray-100 min-w-[150px]">
            <option>All Designations</option>
            <option>CPA</option>
            <option>EA</option>
          </select>
          <select className="p-2 border rounded-md text-gray-900 dark:text-gray-100 min-w-[150px]">
            <option>All Fee Ranges</option>
            <option>$100-$200/hr</option>
            <option>$200-$300/hr</option>
            <option>$300+/hr</option>
          </select>
          <select className="p-2 border rounded-md text-gray-900 dark:text-gray-100 min-w-[150px]">
            <option>All Capacities</option>
            <option>Accepting Clients</option>
            <option>Limited Availability</option>
            <option>Wait List Only</option>
          </select>
        </div>
      </div>

      {/* Professional Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {professionals.map((professional) => (
          <div key={professional.id} className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                  {professional.name}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">{professional.location}</p>
              </div>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  professional.capacity === "accepting"
                    ? "bg-green-100 text-green-800"
                    : "bg-yellow-100 text-yellow-800"
                }`}
              >
                {professional.capacity === "accepting"
                  ? "Accepting Clients"
                  : "Limited Availability"}
              </span>
            </div>

            <div className="mt-4 space-y-3">
              <div>
                <p className="text-sm text-gray-900 dark:text-gray-100">
                  {professional.specialization}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {professional.experience} experience
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                {professional.expertise.map((skill, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {skill}
                  </span>
                ))}
              </div>

              <div className="pt-2 border-t">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      ${professional.hourlyRate}/hour
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500">Billed through RIA</p>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:text-gray-200">
                    1099 Contractor
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  Service fee split: 80% Professional / 10% Advisor / 10% RIA
                </p>
              </div>

              <button
                onClick={() => handleEngageClick(professional)}
                className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Engage Professional
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Engagement Modal */}
      {showEngagementModal && selectedProfessional && (
        <TaxProEngagementModal
          professional={selectedProfessional}
          onClose={() => setShowEngagementModal(false)}
        />
      )}
    </div>
  );
};

// Household Detail View Component
const HouseholdDetailView = ({ household, onClose }) => {
  const [activeTab, setActiveTab] = useState("overview");

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "documents", label: "Documents" },
    { id: "accounts", label: "Accounts" },
    { id: "projections", label: "Projections" },
  ];

  return (
    <Modal 
      isOpen={!!household} 
      onClose={onClose} 
      width="full"    // Use standardized full size for household tax planning
    >
      <div className="bg-surface-default dark:bg-surface-default rounded-lg w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center space-x-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {household.name}
                </h2>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    household.taxProEngagement === "in-network"
                      ? "bg-green-100 text-green-800"
                      : household.taxProEngagement === "out-network"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                  }`}
                >
                  {household.taxProEngagement === "in-network"
                    ? "In-Network Tax Pro"
                    : household.taxProEngagement === "out-network"
                      ? "Out-Network CPA"
                      : "Self-Filing"}
                </span>
              </div>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {household.accountCount} accounts · ${household.aum.toLocaleString()} AUM
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="mt-6">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300"
                  } whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content Area */}
        <div
          className="p-6 overflow-y-auto"
          style={{ maxHeight: "calc(90vh - 200px)" }}
        >
          {activeTab === "overview" && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Filing Status
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-500">Current Status</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {household.filingStatus}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-500">Projected AGI</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        ${household.projectedAgi.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-500">Tax Professional</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {household.taxProfessional || "None assigned"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Tasks & Compliance
                  </h3>
                  <div className="space-y-3">
                    {household.tasks.map((task, index) => (
                      <div
                        key={index}
                        className={`p-3 rounded-lg ${
                          task.priority === "high"
                            ? "bg-red-50"
                            : task.priority === "medium"
                              ? "bg-yellow-50"
                              : "bg-green-50"
                        }`}
                      >
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {task.title}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{task.dueDate}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Quick Actions
                  </h3>
                  <div className="space-y-3">
                    <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                      Request Form 7216
                    </button>
                    <button className="w-full px-4 py-2 border border-gray-300 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800">
                      Generate Tax Summary
                    </button>
                    <button className="w-full px-4 py-2 border border-gray-300 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800">
                      Send Document Request
                    </button>
                  </div>
                </div>
              </div>

              {/* RMD & Tax Loss Harvesting */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                    RMD Status
                  </h3>
                  <div className="space-y-4">
                    {household.rmdAccounts?.map((account, index) => (
                      <div
                        key={index}
                        className="flex justify-between items-center"
                      >
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">
                            {account.name}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            ${account.rmdAmount.toLocaleString()} required
                          </p>
                        </div>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            account.status === "completed"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {account.status === "completed"
                            ? "Completed"
                            : "Pending"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Tax Loss Harvesting
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-500">
                          Available Losses
                        </p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                          $
                          {household.taxLossHarvesting?.availableLosses.toLocaleString()}
                        </p>
                      </div>
                      <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                        View Opportunities
                      </button>
                    </div>
                    <div className="mt-4">
                      <div className="w-full h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-blue-600 rounded-full"
                          style={{
                            width: `${
                              (household.taxLossHarvesting?.harvestedLosses /
                                household.taxLossHarvesting?.totalLosses) *
                              100
                            }%`,
                          }}
                        />
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                        $
                        {household.taxLossHarvesting?.harvestedLosses.toLocaleString()}{" "}
                        harvested of $
                        {household.taxLossHarvesting?.totalLosses.toLocaleString()}{" "}
                        total losses
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "documents" && (
            <div className="space-y-6">
              <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                        Document
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                        Tax Year
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-surface-default dark:bg-surface-default divide-y divide-gray-200">
                    {household.documents?.map((doc, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {doc.name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-gray-100">
                            {doc.year}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              doc.status === "signed"
                                ? "bg-green-100 text-green-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {doc.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-500">
                          <button className="text-blue-600 hover:text-blue-900">
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === "accounts" && (
            <div className="space-y-6">
              {household.accounts?.map((account, index) => (
                <div key={index} className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                        {account.name}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {account.type} · ${account.balance.toLocaleString()}
                      </p>
                    </div>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        account.status === "good"
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {account.status === "good"
                        ? "No Issues"
                        : "Needs Attention"}
                    </span>
                  </div>
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 dark:text-gray-500">YTD Contributions</p>
                      <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
                        ${account.ytdContributions.toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 dark:text-gray-500">Tax Cost Basis</p>
                      <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
                        ${account.costBasis.toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 dark:text-gray-500">Unrealized Gains</p>
                      <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
                        ${account.unrealizedGains.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === "projections" && (
            <div className="space-y-6">
              <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                  Tax Bracket Analysis
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-500">
                        Current Tax Bracket
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {household.currentBracket}%
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-500">
                        Projected Next Year
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {household.projectedBracket}%
                      </p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Recommendations
                    </p>
                    <ul className="mt-2 space-y-2">
                      {household.taxRecommendations?.map((rec, index) => (
                        <li key={index} className="flex items-start">
                          <span className="flex-shrink-0 h-5 w-5 text-green-500">
                            <svg
                              className="h-5 w-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          </span>
                          <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                            {rec}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

const HouseholdViewsTab = () => {
  const [showDetail, setShowDetail] = useState(false);
  const [selectedHousehold, setSelectedHousehold] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const householdsPerPage = 5;

  // Sample household data
  const sampleHouseholds = [
    {
      id: 1,
      name: "Smith Family",
      accountCount: 4,
      aum: 2500000,
      taxProEngagement: "in-network",
      filingStatus: "Married Filing Jointly",
      projectedAgi: 450000,
      taxProfessional: "John Smith, CPA",
      tasks: [
        { title: "Complete RMD", priority: "high", dueDate: "Dec 31, 2024" },
        {
          title: "Review Tax Loss Harvest",
          priority: "medium",
          dueDate: "Ongoing",
        },
      ],
      rmdAccounts: [
        { name: "Traditional IRA", rmdAmount: 45000, status: "pending" },
        { name: "401(k) Rollover", rmdAmount: 32000, status: "completed" },
      ],
      taxLossHarvesting: {
        availableLosses: 15000,
        harvestedLosses: 5000,
        totalLosses: 20000,
      },
      documents: [
        { name: "2023 1040 Return", year: "2023", status: "signed" },
        { name: "2023 W-2", year: "2023", status: "pending" },
      ],
      accounts: [
        {
          name: "Traditional IRA",
          type: "Retirement",
          balance: 850000,
          status: "good",
          ytdContributions: 0,
          costBasis: 750000,
          unrealizedGains: 100000,
        },
        {
          name: "Joint Brokerage",
          type: "Taxable",
          balance: 1650000,
          status: "attention",
          ytdContributions: 50000,
          costBasis: 1500000,
          unrealizedGains: 150000,
        },
      ],
      currentBracket: 32,
      projectedBracket: 35,
      taxRecommendations: [
        "Consider Roth conversion up to 32% bracket limit",
        "Harvest tax losses in brokerage account",
        "Max out 401(k) contributions",
      ],
    },
    {
      id: 2,
      name: "Johnson Family",
      accountCount: 3,
      aum: 1800000,
      taxProEngagement: "out-network",
      filingStatus: "Married Filing Jointly",
      projectedAgi: 380000,
      taxProfessional: "External CPA",
      tasks: [
        { title: "Form 7216 Needed", priority: "high", dueDate: "Immediate" },
        {
          title: "Tax Strategy Review",
          priority: "medium",
          dueDate: "Q2 2024",
        },
      ],
      rmdAccounts: [
        { name: "Traditional IRA", rmdAmount: 28000, status: "completed" },
      ],
      taxLossHarvesting: {
        availableLosses: 22000,
        harvestedLosses: 8000,
        totalLosses: 30000,
      },
      documents: [
        { name: "2023 1040 Return", year: "2023", status: "pending" },
        { name: "2023 K-1", year: "2023", status: "received" },
      ],
      accounts: [
        {
          name: "Traditional IRA",
          type: "Retirement",
          balance: 680000,
          status: "good",
          ytdContributions: 0,
          costBasis: 620000,
          unrealizedGains: 60000,
        },
      ],
      currentBracket: 32,
      projectedBracket: 32,
      taxRecommendations: [
        "Review business expense categorization",
        "Consider qualified business income deduction strategies",
      ],
    },
    {
      id: 3,
      name: "Williams Family",
      accountCount: 5,
      aum: 3200000,
      taxProEngagement: "in-network",
      filingStatus: "Married Filing Jointly",
      projectedAgi: 520000,
      taxProfessional: "Sarah Johnson, EA",
      tasks: [
        { title: "Estate Tax Planning", priority: "high", dueDate: "Q2 2024" },
        {
          title: "Charitable Giving Strategy",
          priority: "medium",
          dueDate: "Ongoing",
        },
      ],
      rmdAccounts: [
        { name: "Traditional IRA", rmdAmount: 52000, status: "pending" },
        { name: "SEP IRA", rmdAmount: 35000, status: "completed" },
      ],
      taxLossHarvesting: {
        availableLosses: 28000,
        harvestedLosses: 12000,
        totalLosses: 40000,
      },
      documents: [
        { name: "2023 1040 Return", year: "2023", status: "signed" },
        { name: "2023 Charitable Receipts", year: "2023", status: "received" },
      ],
      accounts: [
        {
          name: "Family Trust",
          type: "Trust",
          balance: 1200000,
          status: "attention",
          ytdContributions: 0,
          costBasis: 1000000,
          unrealizedGains: 200000,
        },
      ],
      currentBracket: 35,
      projectedBracket: 37,
      taxRecommendations: [
        "Implement charitable remainder trust",
        "Review estate tax exemption strategies",
      ],
    },
    {
      id: 4,
      name: "Brown Family",
      accountCount: 2,
      aum: 950000,
      taxProEngagement: "self-filing",
      filingStatus: "Single",
      projectedAgi: 180000,
      taxProfessional: null,
      tasks: [
        {
          title: "Tax Pro Consultation",
          priority: "medium",
          dueDate: "Q2 2024",
        },
      ],
      rmdAccounts: [],
      taxLossHarvesting: {
        availableLosses: 8000,
        harvestedLosses: 3000,
        totalLosses: 11000,
      },
      documents: [
        { name: "2023 1040 Return", year: "2023", status: "pending" },
      ],
      accounts: [
        {
          name: "Individual Account",
          type: "Taxable",
          balance: 950000,
          status: "good",
          ytdContributions: 25000,
          costBasis: 900000,
          unrealizedGains: 50000,
        },
      ],
      currentBracket: 24,
      projectedBracket: 32,
      taxRecommendations: [
        "Consider tax professional engagement",
        "Review retirement account options",
      ],
    },
    {
      id: 5,
      name: "Davis Family",
      accountCount: 6,
      aum: 4200000,
      taxProEngagement: "in-network",
      filingStatus: "Married Filing Jointly",
      projectedAgi: 850000,
      taxProfessional: "Michael Chen, CPA",
      tasks: [
        {
          title: "International Tax Review",
          priority: "high",
          dueDate: "Q2 2024",
        },
        { title: "FBAR Filing", priority: "high", dueDate: "April 15, 2024" },
      ],
      rmdAccounts: [
        { name: "Traditional IRA", rmdAmount: 65000, status: "pending" },
      ],
      taxLossHarvesting: {
        availableLosses: 45000,
        harvestedLosses: 20000,
        totalLosses: 65000,
      },
      documents: [
        { name: "2023 1040 Return", year: "2023", status: "signed" },
        { name: "2023 International Income", year: "2023", status: "received" },
      ],
      accounts: [
        {
          name: "Global Investment Portfolio",
          type: "Taxable",
          balance: 2800000,
          status: "attention",
          ytdContributions: 100000,
          costBasis: 2500000,
          unrealizedGains: 300000,
        },
      ],
      currentBracket: 37,
      projectedBracket: 37,
      taxRecommendations: [
        "Review foreign tax credit optimization",
        "Consider international estate planning",
      ],
    },
  ];

  // Generate 65 households (13 pages * 5 households per page) by repeating the sample households
  const households = Array(13)
    .fill(sampleHouseholds)
    .flat()
    .map((household, index) => ({
      ...household,
      id: index + 1,
      name:
        Math.floor(index / 5) === 0
          ? household.name
          : `${household.name} ${Math.floor(index / 5) + 1}`, // Only add number if not first page
    }));

  // Get current households for pagination
  const indexOfLastHousehold = currentPage * householdsPerPage;
  const indexOfFirstHousehold = indexOfLastHousehold - householdsPerPage;
  const filteredHouseholds = households.filter(
    (h) =>
      (filterStatus === "all" || h.taxProEngagement === filterStatus) &&
      h.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );
  const currentHouseholds = filteredHouseholds.slice(
    indexOfFirstHousehold,
    indexOfLastHousehold,
  );
  const totalPages = Math.ceil(filteredHouseholds.length / householdsPerPage);

  const handleViewDetail = (household) => {
    setSelectedHousehold(household);
    setShowDetail(true);
  };

  // Pagination component
  const Pagination = () => {
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex justify-center mt-6">
        <nav
          className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
          aria-label="Pagination"
        >
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-surface-default dark:bg-surface-default text-sm font-medium ${
              currentPage === 1
                ? "text-gray-300 cursor-not-allowed"
                : "text-gray-500 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800"
            }`}
          >
            Previous
          </button>
          {pageNumbers.map((number) => (
            <button
              key={number}
              onClick={() => setCurrentPage(number)}
              className={`relative inline-flex items-center px-4 py-2 border ${
                currentPage === number
                  ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                  : "bg-surface-default dark:bg-surface-default border-gray-300 text-gray-500 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800"
              } text-sm font-medium`}
            >
              {number}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-surface-default dark:bg-surface-default text-sm font-medium ${
              currentPage === totalPages
                ? "text-gray-300 cursor-not-allowed"
                : "text-gray-500 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800"
            }`}
          >
            Next
          </button>
        </nav>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search households..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-2 border rounded-md text-gray-900 dark:text-gray-100"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="p-2 border rounded-md text-gray-900 dark:text-gray-100 min-w-[200px]"
          >
            <option value="all">All Tax Engagements</option>
            <option value="in-network">In-Network Tax Pro</option>
            <option value="out-network">Out-Network CPA</option>
            <option value="self-filing">Self-Filing</option>
          </select>
        </div>
      </div>

      {/* Household List */}
      <div className="grid gap-6">
        {currentHouseholds.map((household) => (
          <div key={household.id} className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center space-x-4">
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                    {household.name}
                  </h4>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      household.taxProEngagement === "in-network"
                        ? "bg-green-100 text-green-800"
                        : household.taxProEngagement === "out-network"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-gray-100 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {household.taxProEngagement === "in-network"
                      ? "In-Network Tax Pro"
                      : household.taxProEngagement === "out-network"
                        ? "Out-Network CPA"
                        : "Self-Filing"}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {household.accountCount} accounts · $
                  {household.aum.toLocaleString()} AUM
                </p>
              </div>
              <button
                onClick={() => handleViewDetail(household)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                View Details
              </button>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">RMD Status</p>
                <p className="text-lg font-semibold text-green-600">
                  {household.rmdAccounts?.some((a) => a.status === "pending")
                    ? "Pending"
                    : "Completed"}
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Tax Loss Harvesting
                </p>
                <p className="text-lg font-semibold text-yellow-600">
                  $
                  {household.taxLossHarvesting?.availableLosses.toLocaleString()}{" "}
                  opportunity
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Form 7216 Status
                </p>
                <p className="text-lg font-semibold text-red-600">
                  {household.documents?.some((d) => d.status === "pending")
                    ? "Pending"
                    : "Complete"}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      <Pagination />

      {/* Household Detail Modal */}
      {showDetail && selectedHousehold && (
        <HouseholdDetailView
          household={selectedHousehold}
          onClose={() => setShowDetail(false)}
        />
      )}
    </div>
  );
};

const ToolsCalculatorsTab = () => {
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAvailableOnly, setShowAvailableOnly] = useState(false);

  // Sample client data - would be fetched from your backend
  const clients = [
    {
      id: 1,
      household_id: "245",
      name: "Smith Family",
      missingData: ["retirement account balances", "investment goals"],
      availableData: [
        "income",
        "tax filing status",
        "current savings",
        "charitable contributions",
      ],
    },
    {
      id: 2,
      household_id: "246",
      name: "Johnson Family",
      missingData: ["charitable contributions", "investment goals"],
      availableData: [
        "income",
        "tax filing status",
        "retirement account balances",
      ],
    },
    {
      id: 3,
      household_id: "247",
      name: "Williams Family",
      missingData: [],
      availableData: [
        "income",
        "tax filing status",
        "retirement account balances",
        "investment goals",
        "charitable contributions",
      ],
    },
  ];

  // Filter clients based on search
  const filteredClients = clients.filter((client) =>
    client.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Tax-specific calculators extracted from Financial Calculators
  const taxCalculators = [
    {
      id: "roth-conversion",
      name: "Roth Conversion Calculator",
      description:
        "Evaluate the financial impact of converting a traditional IRA to a Roth IRA, including tax implications.",
      requiredData: [
        "retirement account balances",
        "current tax bracket",
        "expected future tax bracket",
      ],
      category: "Tax Planning",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
      ),
    },
    {
      id: "tax-bracket",
      name: "Tax Bracket Projection",
      description:
        "Predict future tax brackets based on income growth and tax law changes.",
      requiredData: ["income", "tax filing status", "expected income changes"],
      category: "Tax Planning",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
          />
        </svg>
      ),
    },
    {
      id: "capital-gains",
      name: "Capital Gains Calculator",
      description: "Assess tax liabilities from selling investments.",
      requiredData: [
        "investment cost basis",
        "current value",
        "holding period",
      ],
      category: "Tax Planning",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    {
      id: "tax-loss-harvesting",
      name: "Tax Loss Harvesting Calculator",
      description:
        "Evaluate the potential tax benefits of selling underperforming investments.",
      requiredData: ["investment losses", "taxable income", "tax bracket"],
      category: "Tax Planning",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"
          />
        </svg>
      ),
    },
    {
      id: "charitable-contribution",
      name: "Charitable Contribution Calculator",
      description: "Determine how charitable giving affects your tax burden.",
      requiredData: [
        "charitable contributions",
        "tax bracket",
        "itemized deductions",
      ],
      category: "Tax Planning",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
          />
        </svg>
      ),
    },
    {
      id: "rmd",
      name: "RMD Calculator",
      description:
        "Calculate Required Minimum Distributions from retirement accounts.",
      requiredData: ["retirement account balances", "age", "account types"],
      category: "Tax Planning",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    {
      id: "inheritance-tax",
      name: "Inheritance Tax Impact",
      description:
        "Estimate the tax implications of receiving or leaving an inheritance.",
      requiredData: [
        "inheritance amount",
        "state of residence",
        "relationship to deceased",
      ],
      category: "Tax Planning",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
  ];

  // Calculate calculator availability for selected client
  const getClientCalculatorStats = (client) => {
    if (!client) return null;

    let availableCount = 0;
    let totalCount = taxCalculators.length;
    let calculatorStatus = {};

    taxCalculators.forEach((calculator) => {
      const missingForCalc = calculator.requiredData.filter((data) =>
        client.missingData.includes(data),
      );

      const isAvailable = missingForCalc.length === 0;
      calculatorStatus[calculator.id] = {
        available: isAvailable,
        missingData: missingForCalc,
      };

      if (isAvailable) {
        availableCount++;
      }
    });

    return {
      available: availableCount,
      total: totalCount,
      percentage: Math.round((availableCount / totalCount) * 100),
      calculatorStatus,
    };
  };

  const clientStats = selectedClient
    ? getClientCalculatorStats(selectedClient)
    : null;

  // Filter calculators based on search and availability
  const filteredCalculators = taxCalculators.filter((calc) => {
    const matchesSearch =
      calc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      calc.description.toLowerCase().includes(searchQuery.toLowerCase());

    if (!selectedClient || !showAvailableOnly) return matchesSearch;

    const status = clientStats?.calculatorStatus[calc.id];
    return matchesSearch && status?.available;
  });

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
        <div className="flex flex-wrap gap-4">
          {/* Client Search */}
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search clients/households..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full p-2 border rounded-md text-gray-900 dark:text-gray-100"
            />
          </div>

          {/* Client Selection */}
          <select
            value={selectedClient?.id || ""}
            onChange={(e) => {
              const client = clients.find(
                (c) => c.id === parseInt(e.target.value),
              );
              setSelectedClient(client || null);
            }}
            className="p-2 border rounded-md text-gray-900 dark:text-gray-100 min-w-[200px]"
          >
            <option value="">Select Client...</option>
            {filteredClients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>

          {/* Available Only Toggle */}
          {selectedClient && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Show Available Only</span>
              <button
                onClick={() => setShowAvailableOnly(!showAvailableOnly)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  showAvailableOnly ? "bg-blue-600" : "bg-gray-200"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-surface-default dark:bg-surface-default transition-transform ${
                    showAvailableOnly ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          )}
        </div>

        {/* Client Stats */}
        {selectedClient && clientStats && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Available Calculators</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {clientStats.available} of {clientStats.total} (
                  {clientStats.percentage}%)
                </p>
              </div>
              <div className="w-48">
                <div className="h-2 bg-gray-200 rounded-full">
                  <div
                    className="h-2 bg-blue-600 rounded-full"
                    style={{ width: `${clientStats.percentage}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Calculators Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredCalculators.map((calculator) => {
          const status = selectedClient
            ? clientStats?.calculatorStatus[calculator.id]
            : null;
          const isAvailable = !selectedClient || status?.available;

          return (
            <div
              key={calculator.id}
              className={`bg-surface-default dark:bg-surface-default rounded-lg shadow p-6 ${
                selectedClient && !isAvailable ? "opacity-75" : ""
              }`}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                    {calculator.icon}
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-gray-100">
                      {calculator.name}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {calculator.description}
                    </p>
                  </div>
                </div>
                {selectedClient && (
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      isAvailable
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {isAvailable ? "Available" : "Missing Data"}
                  </span>
                )}
              </div>

              <div className="space-y-3">
                {/* Required Data Tags */}
                <div className="flex flex-wrap gap-2">
                  {calculator.requiredData.map((data) => (
                    <span
                      key={data}
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                        selectedClient
                          ? status?.missingData.includes(data)
                            ? "bg-red-50 text-red-700"
                            : "bg-green-50 text-green-700"
                          : "bg-gray-100 text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {data}
                    </span>
                  ))}
                </div>

                {/* Launch Button */}
                <button
                  onClick={() => {
                    if (calculator.id === "rmd") {
                      if (selectedClient?.household_id) {
                        window.location.href = `/advisor-portal/client-database/household-profile/${selectedClient.household_id}/calculators/rmd`;
                      } else {
                        window.location.href =
                          "/financial-planning/calculators/rmd";
                      }
                    } else {
                      window.location.href = `/calculator/${calculator.id}`;
                    }
                  }}
                  disabled={selectedClient && !isAvailable}
                  className={`w-full px-4 py-2 rounded-lg ${
                    selectedClient && !isAvailable
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  {selectedClient && !isAvailable
                    ? "Missing Required Data"
                    : "Launch Calculator"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Compliance KPI Card Component
const ComplianceKpiCard = ({ title, value, color, trend }) => {
  const getColorClasses = (color) => {
    switch (color) {
      case "red":
        return "bg-red-50 text-red-700";
      case "yellow":
        return "bg-yellow-50 text-yellow-700";
      case "green":
        return "bg-green-50 text-green-700";
      default:
        return "bg-gray-50 text-gray-700 dark:text-gray-300";
    }
  };

  return (
    <div className={`p-6 rounded-lg ${getColorClasses(color)}`}>
      <h4 className="text-sm font-medium">{title}</h4>
      <div className="mt-2 flex justify-between items-end">
        <p className="text-2xl font-bold">{value}</p>
        {trend && (
          <span className="text-sm">
            {trend > 0 ? `+${trend}%` : `${trend}%`} vs last month
          </span>
        )}
      </div>
    </div>
  );
};

// Compliance Dashboard Component
const ComplianceDashboard = () => {
  const metrics = [
    { title: "Pending Signatures - Tax Form 7216", value: "12", color: "red", trend: -5 },
    { title: "Open Compliance Tasks", value: "7", color: "yellow", trend: -15 },
    { title: "Overall Compliance", value: "95%", color: "green", trend: 2 },
  ];

  const recentAlerts = [
    {
      id: 1,
      type: "critical",
      message: "3 Form 7216 signatures expiring in 7 days",
      timestamp: "2h ago",
    },
    {
      id: 2,
      type: "warning",
      message: "Unusual document access pattern detected",
      timestamp: "4h ago",
    },
    {
      id: 3,
      type: "info",
      message: "Monthly compliance report generated",
      timestamp: "1d ago",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
          Compliance Dashboard
        </h2>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
          Download Compliance Report
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {metrics.map((metric, index) => (
          <ComplianceKpiCard key={index} {...metric} />
        ))}
      </div>

      {/* Recent Alerts */}
      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Recent Alerts
        </h3>
        <div className="space-y-4">
          {recentAlerts.map((alert) => (
            <div
              key={alert.id}
              className={`p-4 rounded-lg ${
                alert.type === "critical"
                  ? "bg-red-50"
                  : alert.type === "warning"
                    ? "bg-yellow-50"
                    : "bg-blue-50"
              }`}
            >
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{alert.message}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{alert.timestamp}</p>
                </div>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    alert.type === "critical"
                      ? "bg-red-100 text-red-800"
                      : alert.type === "warning"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-blue-100 text-blue-800"
                  }`}
                >
                  {alert.type}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Form 7216 Management Component
const Form7216Management = () => {
  const pendingForms = [
    {
      id: 1,
      name: "Johnson Family",
      lastRequested: "2024-04-01",
      status: "pending",
      notes: "Reminder sent",
    },
    {
      id: 2,
      name: "Smith Family",
      lastRequested: "2024-03-28",
      status: "pending",
      notes: "No response",
    },
    {
      id: 3,
      name: "Williams Family",
      lastRequested: "2024-03-25",
      status: "pending",
      notes: "Client requested paper form",
    },
  ];

  const signedForms = [
    {
      id: 4,
      name: "Davis Family",
      signedDate: "2024-03-15",
      expiryDate: "2025-03-15",
      status: "active",
    },
    {
      id: 5,
      name: "Brown Family",
      signedDate: "2024-03-10",
      expiryDate: "2025-03-10",
      status: "active",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
          Form 7216 Management
        </h2>
        <div className="space-x-4">
          <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800">
            Bulk Send Reminder
          </button>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
            New 7216 Request
          </button>
        </div>
      </div>

      {/* Pending Forms */}
      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Pending Signatures
          </h3>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Household
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Last Requested
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Notes
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-surface-default dark:bg-surface-default divide-y divide-gray-200">
            {pendingForms.map((form) => (
              <tr key={form.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {form.name}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500 dark:text-gray-500">
                    {form.lastRequested}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    {form.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500 dark:text-gray-500">{form.notes}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button className="text-blue-600 hover:text-blue-900">
                    Send Reminder
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Signed Forms */}
      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Active Consents
          </h3>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Household
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Signed Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Expiry Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-surface-default dark:bg-surface-default divide-y divide-gray-200">
            {signedForms.map((form) => (
              <tr key={form.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {form.name}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500 dark:text-gray-500">{form.signedDate}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500 dark:text-gray-500">{form.expiryDate}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {form.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button className="text-blue-600 hover:text-blue-900 mr-4">
                    View PDF
                  </button>
                  <button className="text-red-600 hover:text-red-900">
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Audit Logs Component
const AuditLogs = () => {
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [filterType, setFilterType] = useState("all");

  const auditLogs = [
    {
      id: 1,
      date: "2024-04-01 15:30",
      user: "John Doe",
      action: "Downloaded 2023 1040",
      resource: "Smith Family",
      status: "normal",
    },
    {
      id: 2,
      date: "2024-04-01 14:15",
      user: "Jane Smith",
      action: "Accessed Tax Documents",
      resource: "Johnson Family",
      status: "flagged",
    },
    {
      id: 3,
      date: "2024-04-01 12:00",
      user: "Mike Wilson",
      action: "Shared Documents with CPA",
      resource: "Williams Family",
      status: "normal",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Audit Logs</h2>
        <div className="flex space-x-4">
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) =>
              setDateRange({ ...dateRange, start: e.target.value })
            }
            className="p-2 border rounded-md"
          />
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) =>
              setDateRange({ ...dateRange, end: e.target.value })
            }
            className="p-2 border rounded-md"
          />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="p-2 border rounded-md"
          >
            <option value="all">All Activities</option>
            <option value="downloads">Downloads</option>
            <option value="access">Access</option>
            <option value="sharing">Sharing</option>
          </select>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
            Export Logs
          </button>
        </div>
      </div>

      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Date & Time
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Action
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Resource
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-surface-default dark:bg-surface-default divide-y divide-gray-200">
            {auditLogs.map((log) => (
              <tr key={log.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 dark:text-gray-100">{log.date}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {log.user}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500 dark:text-gray-500">{log.action}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 dark:text-gray-100">{log.resource}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      log.status === "flagged"
                        ? "bg-red-100 text-red-800"
                        : "bg-green-100 text-green-800"
                    }`}
                  >
                    {log.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button className="text-blue-600 hover:text-blue-900">
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Compliance Tasks Component
const ComplianceTaskList = () => {
  const tasks = [
    {
      id: 1,
      title: "Verify Advisor License Renewal",
      description: "Annual verification of Series 65 license for John Doe",
      priority: "high",
      dueDate: "2024-04-15",
      assignedTo: "Compliance Officer",
      status: "pending",
    },
    {
      id: 2,
      title: "Quarterly Wash Sale Review",
      description: "Review flagged wash sale transactions for Q1 2024",
      priority: "medium",
      dueDate: "2024-04-30",
      assignedTo: "Tax Specialist",
      status: "in-progress",
    },
    {
      id: 3,
      title: "Update Privacy Policy",
      description: "Annual review and update of client privacy policy",
      priority: "low",
      dueDate: "2024-05-15",
      assignedTo: "Legal Team",
      status: "pending",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Compliance Tasks</h2>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
          Create New Task
        </button>
      </div>

      <div className="grid gap-6">
        {tasks.map((task) => (
          <div key={task.id} className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  {task.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{task.description}</p>
              </div>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  task.priority === "high"
                    ? "bg-red-100 text-red-800"
                    : task.priority === "medium"
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-green-100 text-green-800"
                }`}
              >
                {task.priority} priority
              </span>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-500">Due Date</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">{task.dueDate}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-500">Assigned To</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">{task.assignedTo}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-500">Status</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">{task.status}</p>
              </div>
            </div>

            <div className="mt-4 flex justify-end space-x-4">
              <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800">
                Reassign
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                Mark Complete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Document Security Component
const DocumentSecurity = () => {
  const documents = [
    {
      id: 1,
      name: "2023 Tax Returns",
      type: "Tax Document",
      encryption: "AES-256",
      access: "Restricted",
      sharing: "External CPA",
      lastAccessed: "2024-04-01",
    },
    {
      id: 2,
      name: "Investment Policy Statement",
      type: "Policy Document",
      encryption: "AES-256",
      access: "Internal Only",
      sharing: "None",
      lastAccessed: "2024-03-28",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Document Security</h2>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
          Security Audit
        </button>
      </div>

      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Document
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Encryption
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Access Level
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                External Sharing
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Last Accessed
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-surface-default dark:bg-surface-default divide-y divide-gray-200">
            {documents.map((doc) => (
              <tr key={doc.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {doc.name}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500 dark:text-gray-500">{doc.type}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {doc.encryption}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 dark:text-gray-100">{doc.access}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500 dark:text-gray-500">{doc.sharing}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500 dark:text-gray-500">
                    {doc.lastAccessed}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button className="text-blue-600 hover:text-blue-900">
                    Manage Access
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ComplianceAuditTab = () => {
  const [viewMode, setViewMode] = useState("dashboard");

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex space-x-4 border-b border-gray-200 dark:border-gray-700 pb-2">
        <button
          onClick={() => setViewMode("dashboard")}
          className={`px-4 py-2 text-sm font-medium ${
            viewMode === "dashboard"
              ? "border-b-2 border-blue-600 text-blue-600"
              : "text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300"
          }`}
        >
          Dashboard
        </button>
        <button
          onClick={() => setViewMode("form7216")}
          className={`px-4 py-2 text-sm font-medium ${
            viewMode === "form7216"
              ? "border-b-2 border-blue-600 text-blue-600"
              : "text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300"
          }`}
        >
          Form 7216
        </button>
        <button
          onClick={() => setViewMode("auditLogs")}
          className={`px-4 py-2 text-sm font-medium ${
            viewMode === "auditLogs"
              ? "border-b-2 border-blue-600 text-blue-600"
              : "text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300"
          }`}
        >
          Audit Logs
        </button>
        <button
          onClick={() => setViewMode("complianceTasks")}
          className={`px-4 py-2 text-sm font-medium ${
            viewMode === "complianceTasks"
              ? "border-b-2 border-blue-600 text-blue-600"
              : "text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300"
          }`}
        >
          Compliance Tasks
        </button>
        <button
          onClick={() => setViewMode("documentSecurity")}
          className={`px-4 py-2 text-sm font-medium ${
            viewMode === "documentSecurity"
              ? "border-b-2 border-blue-600 text-blue-600"
              : "text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300"
          }`}
        >
          Document Security
        </button>
      </div>

      {/* Content Area */}
      <div className="mt-6">
        {viewMode === "dashboard" && <ComplianceDashboard />}
        {viewMode === "form7216" && <Form7216Management />}
        {viewMode === "auditLogs" && <AuditLogs />}
        {viewMode === "complianceTasks" && <ComplianceTaskList />}
        {viewMode === "documentSecurity" && <DocumentSecurity />}
      </div>
    </div>
  );
};

const TaxPlanning = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [notifications] = useState(3); // Sample notification count

  const breadcrumbItems = [
    { label: "Financial Planning", href: "/financial-planning" },
    { label: "Tax Planning", href: "/tax-planning" },
  ];

  const tabs = [
    { id: "dashboard", label: "Dashboard" },
    { id: "taxProfessionals", label: "Tax Professionals" },
    { id: "households", label: "Households" },
    { id: "toolsCalculators", label: "Tools & Calculators" },
    { id: "compliance", label: "Compliance & Audit" },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "dashboard":
        return <DashboardTab />;
      case "taxProfessionals":
        return <TaxProNetworkTab />;
      case "households":
        return <HouseholdViewsTab />;
      case "toolsCalculators":
        return <ToolsCalculatorsTab />;
      case "compliance":
        return <ComplianceAuditTab />;
      default:
        return <DashboardTab />;
    }
  };

  return (
    <SubPageLayout
      breadcrumbItems={breadcrumbItems}
      title="Tax Planning"
      subtitle="Seamlessly integrate tax planning & compliance"
      actionButton={
        <div className="flex items-center space-x-4">
          <NotificationBell count={notifications} />
          <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
            Add Tax Strategy
          </button>
        </div>
      }
    >
      {/* Global Search */}
      <div className="mb-6">
        <GlobalSearch />
      </div>

      {/* Tab Navigation */}
      <nav className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <ul className="flex space-x-8">
          {tabs.map((tab) => (
            <li
              key={tab.id}
              className={`cursor-pointer pb-4 ${
                activeTab === tab.id
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300"
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </li>
          ))}
        </ul>
      </nav>

      {/* Tab Content */}
      <div className="tab-content">{renderTabContent()}</div>

      {/* Tooltips */}
      <Tooltip id="task-tooltip" />
    </SubPageLayout>
  );
};

export default TaxPlanning;
